import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { submitFormOnEnter } from '@databases/form/Form';
import { SeamlessTextArea } from '@databases/form/styled';
import { Metrics } from '@databases/frontend-metrics';
import { useTypingPerformance } from '@databases/frontend-metrics/useTypingPerformance';
import { useTextDelta } from '@databases/react/useTextDelta';
import { DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { ValueChange } from '@databases/utils/database/changesets';
import composeRefs from '@seznam/compose-react-refs';
import { Op } from 'quill-delta';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  MAX_POPOVER_EDIT_WIDTH,
  TEXT_VALUE_MAX_TEXT_AREA_HEIGHT,
} from './popover-edit-common';
import { EditComponentProps } from './types';

interface TextValuePopoverEditProps {
  field: DatabaseField;
  onChange: (value: string, change: ValueChange | null) => void;
  onFocus?: (e?: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e?: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  value: string;
  isSeamless?: boolean;
  isDefaultValue?: boolean;
  isFilterValueEdit?: boolean;
}

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 'dialog', // Ensure container sits above blue dots
});

const textAreaMinHeightStyling = xcss({
  minHeight: 'size.400',
});

const TextValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.TEXT>
> = ({
  field,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isSeamless,
  isDefaultValue,
  isFilterValueEdit,
}: TextValuePopoverEditProps) => {
  const maxWidthReachedRef = useRef<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const resolvedValue = field.settings.newlines
    ? value
    : value.replace(/\r\n|\n|\r/g, ' ');
  const [inputValue, setInputValue] = useState(resolvedValue);

  const adjustTextAreaDimensions = useCallback(() => {
    const textArea = textAreaRef.current;

    if (textArea) {
      textArea.style.minHeight = '100%';
      textArea.style.height = '100%';
      textArea.style.minWidth = 'auto';
      textArea.style.whiteSpace = 'nowrap';

      const width = Math.min(textArea.scrollWidth, MAX_POPOVER_EDIT_WIDTH);

      if (width >= MAX_POPOVER_EDIT_WIDTH && !maxWidthReachedRef.current) {
        maxWidthReachedRef.current = true;
      } else if (width < MAX_POPOVER_EDIT_WIDTH && maxWidthReachedRef.current) {
        maxWidthReachedRef.current = false;
      }

      if (textArea.style.minWidth !== `${width}px`) {
        textArea.style.minWidth = `${width}px`;
      }

      if (maxWidthReachedRef.current) {
        const middleOfScreen = window.innerHeight / 2;
        const topOfTextArea = textArea.getBoundingClientRect().top;
        const textAreaContainerHeight = textArea.parentElement?.clientHeight || 0;
        textArea.style.whiteSpace = 'normal';
        // Text area height will retain cell height at a minimum and expand vertically
        textArea.style.height = `${Math.max(
          textAreaContainerHeight,
          textArea.scrollHeight
        )}px`;

        // Text area opens "downwards" if above middle of screen and "upwards" if below
        if (topOfTextArea > middleOfScreen) {
          textArea.style.bottom = '0px';
        } else {
          textArea.style.top = '0px';
        }
      }
    }
  }, []);

  useEffect(() => {
    adjustTextAreaDimensions();
  }, [inputValue, adjustTextAreaDimensions]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!field.settings.newlines) {
        submitFormOnEnter(e);
      }
    },
    [field.settings.newlines]
  );

  const Component = isSeamless ? SeamlessTextArea : TextArea;

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );

      adjustTextAreaDimensions();
      onFocus?.(e);
    },
    [onFocus, adjustTextAreaDimensions]
  );

  useTextDelta(
    textAreaRef.current,
    useCallback(
      (delta: Op[], newValue: string) => {
        setInputValue(newValue);
        onChange(newValue, {
          type: DatabaseFieldType.TEXT,
          delta,
        });
      },
      [onChange]
    )
  );

  const typingPerformanceRef = useTypingPerformance<HTMLTextAreaElement>(
    Metrics.MEAN_TEXT_VALUE_TYPING
  );
  return (
    <Box
      xcss={[
        textAreaContainerStyling,
        (isDefaultValue || isFilterValueEdit) && textAreaMinHeightStyling,
      ]}
    >
      <Component
        value={inputValue}
        onFocus={handleFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        onKeyDown={handleKeyDown}
        resize="none"
        wrap="off"
        ref={composeRefs(textAreaRef, typingPerformanceRef)}
        testId={'text-value-edit'}
        style={{
          border: `2px solid ${token('color.background.accent.blue.subtle', '#579DFF')}`,
          borderRadius: 'border.radius.100',
          overflow: 'hidden',
          position: 'absolute',
          boxSizing: 'border-box',
          maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
        }}
      />
    </Box>
  );
};

export default TextValuePopoverEdit;
